<script setup lang="ts">
import { computed, ref } from 'vue';
import MenuPanel from './ContactBubble/MenuPanel.vue';
import MenuItem from './ContactBubble/MenuItem.vue';
import { useContactLinks } from './ContactBubble/useContactLinks';
import Icon from '@/components/Icon.vue';
import { UrlProp, useUrl } from '@/composables/useUrl';

type Corner = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

const props = withDefaults(
    defineProps<{
        phone?: string;
        email?: string;
        address?: string;
        contact_url?: UrlProp;
        menu_id?: string;
        corner?: Corner;
        map_provider?: 'google' | 'osm';
    }>(),
    {
        corner: 'bottom-right',
        map_provider: 'google',
    }
);

const isOpen = ref(false);
const { phoneHref, emailHref, mapHref } = useContactLinks(props);

const fallbackId = computed(() => {
    const base = [
        props.email ?? '',
        props.phone ?? '',
        props.contact_url ?? '',
        props.address ?? '',
    ].join('|');
    let h = 2166136261 >>> 0;
    for (let i = 0; i < base.length; i++) {
        h ^= base.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
    }
    return `contact-bubble-${h.toString(16)}`;
});
const menuId = computed(() => props.menu_id || fallbackId.value);

const panelPos = computed(() => {
    switch (props.corner) {
        case 'top-left':
            return {
                box: 'absolute left-0 mt-2', // opens downward & left
                origin: 'origin-top-left',
                arrow: 'top-0 left-6 -translate-y-1/2', // caret at top, left-ish
            };
        case 'top-right':
            return {
                box: 'absolute right-0 mt-2', // opens downward & right
                origin: 'origin-top-right',
                arrow: 'top-0 right-6 -translate-y-1/2',
            };
        case 'bottom-right':
            return {
                box: 'absolute right-0 bottom-full mb-2', // opens upward & right
                origin: 'origin-bottom-right',
                arrow: 'bottom-0 right-6 translate-y-1/2',
            };
        case 'bottom-left':
        default:
            return {
                box: 'absolute left-0 bottom-full mb-2', // opens upward & left
                origin: 'origin-bottom-left',
                arrow: 'bottom-0 left-6 translate-y-1/2',
            };
    }
});

const contactUrl = computed(() => {
    const { url } = useUrl(props.contact_url, '#');
    return url();
});

function toggle() {
    isOpen.value = !isOpen.value;
}

function close() {
    isOpen.value = false;
}
</script>

<template>
    <div class="relative inline-block">
        <button
            class="cb-trigger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 transition-transform"
            :aria-expanded="isOpen ? 'true' : 'false'"
            :aria-controls="menuId"
            aria-label="Contact menu"
            @click="toggle"
            @keydown.enter.prevent="toggle"
        >
            <Icon name="admin-comments" class="text-blue-700" />
        </button>

        <transition name="cb-pop" appear>
            <MenuPanel
                v-if="isOpen"
                :menuId="menuId"
                :class="[panelPos.box, panelPos.origin]"
                :arrow-class="panelPos.arrow"
                @close="close"
            >
                <li v-if="phone">
                    <MenuItem :href="phoneHref" title="Call" :subtitle="phone" icon="phone" />
                </li>
                <li v-if="email">
                    <MenuItem :href="emailHref" title="Email" :subtitle="email" icon="email" />
                </li>
                <li v-if="address">
                    <MenuItem
                        :href="mapHref"
                        title="Find us"
                        :subtitle="address"
                        icon="location-alt"
                        :external="true"
                    />
                </li>
                <li v-if="contact_url">
                    <MenuItem
                        :href="contactUrl"
                        title="Contact Us"
                        :subtitle="contactUrl"
                        icon="feedback"
                    />
                </li>
            </MenuPanel>
        </transition>
    </div>
</template>

<style scoped>
@media (prefers-reduced-motion: no-preference) {
    .cb-pop-enter-active,
    .cb-pop-leave-active {
        transition:
            opacity 0.16s ease,
            transform 0.18s cubic-bezier(0.22, 0.61, 0.36, 1);
        will-change: opacity, transform;
    }
    .cb-pop-enter-from,
    .cb-pop-leave-to {
        opacity: 0;
        transform: translateY(8px); /* integer px → better rasterization */
    }
}

/* On small screens, avoid any transform during animation */
@media (max-width: 640px) {
    .cb-pop-enter-active,
    .cb-pop-leave-active {
        transition: opacity 0.14s ease;
    }
    .cb-pop-enter-from,
    .cb-pop-leave-to {
        transform: none;
    }
}

.cb-trigger {
    /* layout */
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.5rem;
    height: 3.5rem;
    font-size: 2rem; /* 12 / 3xl */
    border-radius: 9999px;
    cursor: pointer;

    /* modern light look */
    background: linear-gradient(180deg, #fff, #f7f9fb);
    border: 1px solid #e6eaf0;
    box-shadow:
        0 10px 20px -10px rgba(27, 60, 138, 0.15),
        0 2px 6px -2px rgba(15, 23, 42, 0.08);

    transition:
        transform 0.15s ease,
        box-shadow 0.2s ease,
        background-color 0.15s ease;
}
.cb-trigger:hover {
    transform: translateY(-1px) scale(1.02);
    box-shadow:
        0 14px 28px -12px rgba(27, 60, 138, 0.18),
        0 4px 10px -3px rgba(15, 23, 42, 0.1);
}
.cb-trigger:active {
    transform: translateY(0) scale(0.98);
}

@media (hover: none) and (pointer: coarse) {
    .cb-trigger:hover,
    .cb-trigger:active {
        transform: none !important;
    }
}
</style>
